import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Form, FormField, Input, Modal, Select, Spinner, Tabs, Textarea } from '@vwfs-bronson/bronson-react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { AuthTokens } from '../../auth';
import {
  Agent,
  FesConfig,
  JiraProject,
  Team,
  TriggerTokenChannel,
  TriggerTokenCreatedResponse,
  UnclaimedInstallation,
  createTeam,
  createTriggerToken,
  createWebhookKey,
  deleteWebhookKey,
  getAgents,
  getTeam,
  getUnclaimedInstallations,
  getWebhookKeys,
  installTeam,
  updateTeam,
  WebhookKeyCreatedResponse,
} from '../../fes';
import {
  TeamFormState,
  TeamPanelMode,
  effectiveJiraConnectionMode,
  emptyTeamForm,
  isTeamStatus,
  jiraProjectKeyPattern,
  teamToForm,
} from './teamForm';
import { Session } from '../../session';
import { TeamDetailPanel } from './TeamDetailPanel';
import { TeamMembersPanel } from './TeamMembersPanel';
import { TeamsTable } from './TeamsTable';
import { TriggerTokensPanel } from './TriggerTokensPanel';
import { AzureDevOpsPanel, hasActiveAzureDevOpsToken } from './AzureDevOpsPanel';
import { WebhookKeysTable } from './WebhookKeysTable';
import { GitHubPrTriggersPanel } from './GitHubPrTriggersPanel';

type CreateTeamFormState = {
  name: string;
  description: string;
  installationId: string;
  generateTriggerToken: boolean;
  triggerTokenChannel: TriggerTokenChannel;
  triggerTokenProjectKey: string;
  triggerTokenDefaultAgentId: string;
  triggerTokenAllowedAgentIds: string[];
  triggerTokenDescription: string;
  generateWebhookKey: boolean;
  projectKey: string;
  agentId: string;
  webhookKeyDescription: string;
};

type CreateWebhookKeyFormState = {
  projectKey: string;
  agentId: string;
  description: string;
};

const emptyCreateTeamForm: CreateTeamFormState = {
  name: '',
  description: '',
  installationId: '',
  generateTriggerToken: false,
  triggerTokenChannel: 'jira',
  triggerTokenProjectKey: '',
  triggerTokenDefaultAgentId: '',
  triggerTokenAllowedAgentIds: [],
  triggerTokenDescription: '',
  generateWebhookKey: false,
  projectKey: '',
  agentId: '',
  webhookKeyDescription: '',
};

const emptyCreateWebhookKeyForm: CreateWebhookKeyFormState = {
  projectKey: '',
  agentId: '',
  description: '',
};

export function TeamsView({
  fesConfig,
  tokens,
  teams,
  session,
  canEdit,
  onTeamSaved,
}: {
  fesConfig: FesConfig;
  tokens: AuthTokens;
  teams: Team[];
  session: Session;
  canEdit: boolean;
  onTeamSaved: (team: Team) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const teamRouteMatch = matchPath('/teams/:teamId', location.pathname);
  const routeTeamId = teamRouteMatch?.params.teamId ? decodeURIComponent(teamRouteMatch.params.teamId) : undefined;
  const latestTeamRequest = useRef(0);
  const teamDetailRef = useRef<HTMLDivElement>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string>();
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [form, setForm] = useState<TeamFormState>(emptyTeamForm);
  const [mode, setMode] = useState<TeamPanelMode>('read');
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [panelError, setPanelError] = useState<string>();
  const [saveError, setSaveError] = useState<string>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTeamFormState>(emptyCreateTeamForm);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [createError, setCreateError] = useState<string>();
  const [unclaimedInstallations, setUnclaimedInstallations] = useState<UnclaimedInstallation[]>([]);
  const [areInstallationsLoading, setAreInstallationsLoading] = useState(false);
  const [installationsError, setInstallationsError] = useState<string>();
  const [createFlowTeam, setCreateFlowTeam] = useState<Team | null>(null);
  const [webhookKeys, setWebhookKeys] = useState<JiraProject[]>([]);
  const [areWebhookKeysLoading, setAreWebhookKeysLoading] = useState(false);
  const [webhookKeysError, setWebhookKeysError] = useState<string>();
  const [webhookKeyToRevoke, setWebhookKeyToRevoke] = useState<JiraProject | null>(null);
  const [isRevokingWebhookKey, setIsRevokingWebhookKey] = useState(false);
  const [revokeWebhookKeyError, setRevokeWebhookKeyError] = useState<string>();
  const [isGenerateWebhookKeyModalOpen, setIsGenerateWebhookKeyModalOpen] = useState(false);
  const [createWebhookKeyForm, setCreateWebhookKeyForm] =
    useState<CreateWebhookKeyFormState>(emptyCreateWebhookKeyForm);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [areAgentsLoading, setAreAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string>();
  const [isGeneratingWebhookKey, setIsGeneratingWebhookKey] = useState(false);
  const [generateWebhookKeyError, setGenerateWebhookKeyError] = useState<string>();
  const [generatedWebhookKey, setGeneratedWebhookKey] = useState<WebhookKeyCreatedResponse['data']>();
  const [generatedTriggerToken, setGeneratedTriggerToken] = useState<TriggerTokenCreatedResponse['data']>();
  const [copyWebhookKeyStatus, setCopyWebhookKeyStatus] = useState<string>();
  const [copyTriggerTokenStatus, setCopyTriggerTokenStatus] = useState<string>();
  const [activeTeamTab, setActiveTeamTab] = useState('overview');
  const [listSearch, setListSearch] = useState('');

  useEffect(() => {
    if (!selectedTeamId) {
      return;
    }

    teamDetailRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [isDetailLoading, selectedTeamId]);

  const handleSelect = (team: Team, updateRoute = true) => {
    if (!team.teamId) {
      return;
    }

    const requestId = latestTeamRequest.current + 1;
    latestTeamRequest.current = requestId;
    setSelectedTeamId(team.teamId);
    setSelectedTeam(team);
    setForm(teamToForm(team));
    setMode('read');
    setPanelError(undefined);
    setSaveError(undefined);
    setActiveTeamTab('overview');
    if (updateRoute) {
      navigate(`/teams/${encodeURIComponent(team.teamId)}`);
    }
    setIsDetailLoading(true);
    setWebhookKeys([]);
    setWebhookKeysError(undefined);
    setAreWebhookKeysLoading(true);

    getWebhookKeys(fesConfig, tokens, team.teamId)
      .then(webhookKeysResponse => {
        if (latestTeamRequest.current !== requestId) {
          return;
        }

        setWebhookKeys(webhookKeysResponse.data || []);
      })
      .catch(error => {
        if (latestTeamRequest.current !== requestId) {
          return;
        }

        setWebhookKeysError(error instanceof Error ? error.message : 'Unable to load webhook keys.');
      })
      .finally(() => {
        if (latestTeamRequest.current === requestId) {
          setAreWebhookKeysLoading(false);
        }
      });

    getTeam(fesConfig, tokens, team.teamId)
      .then(teamResponse => {
        if (latestTeamRequest.current !== requestId) {
          return;
        }

        const detailedTeam = teamResponse.data || team;
        setSelectedTeam(detailedTeam);
        setForm(teamToForm(detailedTeam));
      })
      .catch(error => {
        if (latestTeamRequest.current !== requestId) {
          return;
        }

        setPanelError(error instanceof Error ? error.message : 'Unable to load team details.');
      })
      .finally(() => {
        if (latestTeamRequest.current === requestId) {
          setIsDetailLoading(false);
        }
      });
  };

  useEffect(() => {
    if (!routeTeamId || routeTeamId === selectedTeamId) {
      return;
    }

    const routedTeam = teams.find(team => team.teamId === routeTeamId);
    if (routedTeam) {
      handleSelect(routedTeam, false);
    }
    // Route changes are the trigger; handleSelect intentionally owns the data-loading workflow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeTeamId, selectedTeamId, teams]);

  const handleFormChange = (field: keyof TeamFormState, value: string) => {
    setForm(currentForm => {
      if (field === 'status') {
        return isTeamStatus(value) ? { ...currentForm, status: value } : currentForm;
      }
      if (field === 'jiraConnectionMode') {
        return value === 'default' || value === 'dedicated' || value === 'disabled'
          ? {
              ...currentForm,
              jiraConnectionMode: value,
              ...(value === 'dedicated' ? {} : { jiraBaseUrl: '' }),
            }
          : currentForm;
      }

      return {
        ...currentForm,
        [field]: value,
      };
    });
  };

  const handleEdit = () => {
    setMode('edit');
    setPanelError(undefined);
    setSaveError(undefined);
  };

  const handleCancel = () => {
    if (selectedTeam) {
      setForm(teamToForm(selectedTeam));
    }

    setMode('read');
    setPanelError(undefined);
    setSaveError(undefined);
  };

  const handleSaveErrorClose = () => {
    setSaveError(undefined);
  };

  const handleCreateOpen = () => {
    setCreateForm({
      ...emptyCreateTeamForm,
      agentId: agents[0]?.agentId || '',
    });
    setCreateError(undefined);
    setInstallationsError(undefined);
    setGeneratedWebhookKey(undefined);
    setGeneratedTriggerToken(undefined);
    setCopyWebhookKeyStatus(undefined);
    setCopyTriggerTokenStatus(undefined);
    setCreateFlowTeam(null);
    setIsCreateModalOpen(true);
    loadUnclaimedInstallationsForCreateForm();
  };

  const handleCreateClose = () => {
    if (isCreatingTeam) {
      return;
    }

    if ((generatedWebhookKey || generatedTriggerToken) && createFlowTeam) {
      handleCreateTeamResultClose();
      return;
    }

    setIsCreateModalOpen(false);
    setCreateForm(emptyCreateTeamForm);
    setCreateError(undefined);
    setInstallationsError(undefined);
    setGeneratedWebhookKey(undefined);
    setGeneratedTriggerToken(undefined);
    setCopyWebhookKeyStatus(undefined);
    setCopyTriggerTokenStatus(undefined);
    setCreateFlowTeam(null);
  };

  const handleCreateFormChange = (field: keyof CreateTeamFormState, value: string | boolean) => {
    setCreateForm(currentForm => ({
      ...currentForm,
      [field]:
        (field === 'projectKey' || field === 'triggerTokenProjectKey') && typeof value === 'string'
          ? value.toUpperCase()
          : value,
    }));
    setCreateError(undefined);
  };

  const handleCreateGenerateTriggerTokenChange = (checked: boolean) => {
    setCreateForm(currentForm => ({
      ...currentForm,
      generateTriggerToken: checked,
    }));
    setCreateError(undefined);

    if (checked) {
      loadAgentsForWebhookKeyForm('create');
    }
  };

  const handleCreateTriggerTokenChannelChange = (channel: TriggerTokenChannel) => {
    setCreateForm(currentForm => ({
      ...currentForm,
      triggerTokenChannel: channel,
      triggerTokenProjectKey: channel === 'jira' ? currentForm.triggerTokenProjectKey : '',
    }));
    setCreateError(undefined);
  };

  const handleCreateTriggerTokenAgentToggle = (agentId: string, checked: boolean) => {
    setCreateForm(currentForm => ({
      ...currentForm,
      triggerTokenAllowedAgentIds: checked
        ? Array.from(new Set([...currentForm.triggerTokenAllowedAgentIds, agentId]))
        : currentForm.triggerTokenAllowedAgentIds.filter(id => id !== agentId),
    }));
    setCreateError(undefined);
  };

  const handleCreateGenerateWebhookKeyChange = (checked: boolean) => {
    setCreateForm(currentForm => ({
      ...currentForm,
      generateWebhookKey: checked,
      agentId: checked ? currentForm.agentId || agents[0]?.agentId || '' : currentForm.agentId,
    }));
    setCreateError(undefined);

    if (checked) {
      loadAgentsForWebhookKeyForm('create');
    }
  };

  const handleCreateTeam = () => {
    const name = createForm.name.trim();

    if (!name) {
      setCreateError('Name is required.');
      return;
    }

    const selectedInstallationId = createForm.installationId.trim();
    const parsedInstallationId = Number(selectedInstallationId);

    if (
      selectedInstallationId &&
      (!/^\d+$/.test(selectedInstallationId) ||
        !Number.isSafeInteger(parsedInstallationId) ||
        parsedInstallationId <= 0)
    ) {
      setCreateError('Installation ID must be a positive integer.');
      return;
    }

    if (createForm.generateWebhookKey && !selectedInstallationId) {
      setCreateError('Installation ID is required to generate a webhook key.');
      return;
    }

    const projectKey = createForm.projectKey.trim().toUpperCase();

    if (createForm.generateWebhookKey && !jiraProjectKeyPattern.test(projectKey)) {
      setCreateError(
        'Project key must start with an uppercase letter and contain 2-10 uppercase letters, numbers, or underscores.'
      );
      return;
    }

    if (createForm.generateWebhookKey && !createForm.agentId) {
      setCreateError('Agent is required.');
      return;
    }

    const triggerTokenProjectKey = createForm.triggerTokenProjectKey.trim().toUpperCase();

    if (
      createForm.generateTriggerToken &&
      createForm.triggerTokenChannel === 'jira' &&
      triggerTokenProjectKey &&
      !jiraProjectKeyPattern.test(triggerTokenProjectKey)
    ) {
      setCreateError(
        'Trigger token project key must start with an uppercase letter and contain 2-10 uppercase letters, numbers, or underscores.'
      );
      return;
    }

    setIsCreatingTeam(true);
    setCreateError(undefined);
    setCopyWebhookKeyStatus(undefined);
    setCopyTriggerTokenStatus(undefined);

    const create = async () => {
      try {
        const description = createForm.description.trim();
        const webhookKeyDescription = createForm.webhookKeyDescription.trim();
        const triggerTokenDescription = createForm.triggerTokenDescription.trim();
        const teamResponse = await createTeam(fesConfig, tokens, {
          name,
          ...(description ? { description } : {}),
        });
        const createdTeam = teamResponse.data;

        if (!createdTeam?.teamId) {
          throw new Error('FES did not return a team ID for the created team.');
        }

        let savedTeam = createdTeam;

        if (selectedInstallationId) {
          const installResponse = await installTeam(fesConfig, tokens, createdTeam.teamId, {
            installationId: parsedInstallationId,
          });
          savedTeam = installResponse.data || {
            ...createdTeam,
            installationId: parsedInstallationId,
          };
        }

        if (createForm.generateWebhookKey || createForm.generateTriggerToken) {
          setCreateFlowTeam(savedTeam);
        }

        if (createForm.generateWebhookKey) {
          const webhookKeyResponse = await createWebhookKey(fesConfig, tokens, savedTeam.teamId, {
            projectKey,
            agentId: createForm.agentId,
            ...(webhookKeyDescription ? { description: webhookKeyDescription } : {}),
          });

          if (!webhookKeyResponse.data?.token) {
            throw new Error('FES did not return a generated webhook key.');
          }

          setGeneratedWebhookKey(webhookKeyResponse.data);
        }

        if (createForm.generateTriggerToken) {
          const triggerTokenResponse = await createTriggerToken(fesConfig, tokens, savedTeam.teamId, {
            channel: createForm.triggerTokenChannel,
            ...(createForm.triggerTokenChannel === 'jira' && triggerTokenProjectKey
              ? { projectKey: triggerTokenProjectKey }
              : {}),
            ...(createForm.triggerTokenDefaultAgentId
              ? { defaultAgentId: createForm.triggerTokenDefaultAgentId }
              : {}),
            ...(createForm.triggerTokenAllowedAgentIds.length > 0
              ? { allowedAgentIds: createForm.triggerTokenAllowedAgentIds }
              : {}),
            ...(triggerTokenDescription ? { description: triggerTokenDescription } : {}),
          });

          if (!triggerTokenResponse.data?.token) {
            throw new Error('FES did not return a generated trigger token.');
          }

          setGeneratedTriggerToken(triggerTokenResponse.data);
        }

        if (createForm.generateWebhookKey || createForm.generateTriggerToken) {
          return;
        }

        setIsCreateModalOpen(false);
        setCreateForm(emptyCreateTeamForm);
        setCreateFlowTeam(null);
        onTeamSaved(savedTeam);
        handleSelect(savedTeam);
      } catch (error) {
        setCreateError(error instanceof Error ? error.message : 'Unable to complete team onboarding.');
      } finally {
        setIsCreatingTeam(false);
      }
    };

    void create();
  };

  const handleCreateTeamResultClose = () => {
    const savedTeam = createFlowTeam;

    setIsCreateModalOpen(false);
    setCreateForm(emptyCreateTeamForm);
    setCreateError(undefined);
    setInstallationsError(undefined);
    setGeneratedWebhookKey(undefined);
    setGeneratedTriggerToken(undefined);
    setCopyWebhookKeyStatus(undefined);
    setCopyTriggerTokenStatus(undefined);
    setCreateFlowTeam(null);

    if (savedTeam) {
      onTeamSaved(savedTeam);
      handleSelect(savedTeam);
    }
  };

  const loadUnclaimedInstallationsForCreateForm = () => {
    setAreInstallationsLoading(true);
    setInstallationsError(undefined);

    const loadInstallations = async () => {
      try {
        const installationsResponse = await getUnclaimedInstallations(fesConfig, tokens);
        setUnclaimedInstallations(installationsResponse.data || []);
      } catch (error) {
        setInstallationsError(error instanceof Error ? error.message : 'Unable to load unclaimed installations.');
      } finally {
        setAreInstallationsLoading(false);
      }
    };

    void loadInstallations();
  };

  const refreshWebhookKeys = async (teamId: string, requestId = latestTeamRequest.current) => {
    setAreWebhookKeysLoading(true);
    setWebhookKeysError(undefined);

    try {
      const webhookKeysResponse = await getWebhookKeys(fesConfig, tokens, teamId);

      if (latestTeamRequest.current === requestId) {
        setWebhookKeys(webhookKeysResponse.data || []);
      }
    } catch (error) {
      if (latestTeamRequest.current === requestId) {
        setWebhookKeysError(error instanceof Error ? error.message : 'Unable to load webhook keys.');
      }
    } finally {
      if (latestTeamRequest.current === requestId) {
        setAreWebhookKeysLoading(false);
      }
    }
  };

  const loadAgentsForWebhookKeyForm = (target: 'standalone' | 'create' = 'standalone') => {
    if (agents.length > 0) {
      setAgentsError(undefined);
      if (target === 'create') {
        setCreateForm(currentForm => ({
          ...currentForm,
          agentId: currentForm.agentId || agents[0].agentId,
        }));
      } else {
        setCreateWebhookKeyForm(currentForm => ({
          ...currentForm,
          agentId: currentForm.agentId || agents[0].agentId,
        }));
      }
      return;
    }

    setAreAgentsLoading(true);
    setAgentsError(undefined);

    const loadAgents = async () => {
      try {
        const agentsResponse = await getAgents(fesConfig, tokens);
        const loadedAgents = agentsResponse.data || [];

        setAgents(loadedAgents);
        setAgentsError(loadedAgents.length === 0 ? 'No agents found.' : undefined);
        if (target === 'create') {
          setCreateForm(currentForm => ({
            ...currentForm,
            agentId: currentForm.agentId || loadedAgents[0]?.agentId || '',
          }));
        } else {
          setCreateWebhookKeyForm(currentForm => ({
            ...currentForm,
            agentId: currentForm.agentId || loadedAgents[0]?.agentId || '',
          }));
        }
      } catch (error) {
        setAgentsError(error instanceof Error ? error.message : 'Unable to load agents.');
      } finally {
        setAreAgentsLoading(false);
      }
    };

    void loadAgents();
  };

  const handleGenerateWebhookKeyOpen = () => {
    if (!(selectedTeamId || selectedTeam?.teamId)) {
      return;
    }

    setCreateWebhookKeyForm({
      ...emptyCreateWebhookKeyForm,
      agentId: agents[0]?.agentId || '',
    });
    setGenerateWebhookKeyError(undefined);
    setGeneratedWebhookKey(undefined);
    setCopyWebhookKeyStatus(undefined);
    setIsGenerateWebhookKeyModalOpen(true);
    loadAgentsForWebhookKeyForm();
  };

  const handleGenerateWebhookKeyClose = () => {
    if (isGeneratingWebhookKey) {
      return;
    }

    setIsGenerateWebhookKeyModalOpen(false);
    setCreateWebhookKeyForm(emptyCreateWebhookKeyForm);
    setGenerateWebhookKeyError(undefined);
    setGeneratedWebhookKey(undefined);
    setCopyWebhookKeyStatus(undefined);
  };

  const handleGenerateWebhookKeyResultClose = () => {
    const teamId = selectedTeamId || selectedTeam?.teamId;
    const requestId = latestTeamRequest.current;

    handleGenerateWebhookKeyClose();

    if (teamId) {
      void refreshWebhookKeys(teamId, requestId);
    }
  };

  const handleCreateWebhookKeyFormChange = (field: keyof CreateWebhookKeyFormState, value: string) => {
    setCreateWebhookKeyForm(currentForm => ({
      ...currentForm,
      [field]: field === 'projectKey' ? value.toUpperCase() : value,
    }));
    setGenerateWebhookKeyError(undefined);
  };

  const handleCreateWebhookKey = () => {
    const teamId = selectedTeamId || selectedTeam?.teamId;

    if (!teamId) {
      return;
    }

    const projectKey = createWebhookKeyForm.projectKey.trim().toUpperCase();

    if (!jiraProjectKeyPattern.test(projectKey)) {
      setGenerateWebhookKeyError(
        'Project key must start with an uppercase letter and contain 2-10 uppercase letters, numbers, or underscores.'
      );
      return;
    }

    if (!createWebhookKeyForm.agentId) {
      setGenerateWebhookKeyError('Agent is required.');
      return;
    }

    const description = createWebhookKeyForm.description.trim();

    setIsGeneratingWebhookKey(true);
    setGenerateWebhookKeyError(undefined);
    setCopyWebhookKeyStatus(undefined);

    const generate = async () => {
      try {
        const webhookKeyResponse = await createWebhookKey(fesConfig, tokens, teamId, {
          projectKey,
          agentId: createWebhookKeyForm.agentId,
          ...(description ? { description } : {}),
        });

        if (!webhookKeyResponse.data?.token) {
          throw new Error('FES did not return a generated webhook key.');
        }

        setGeneratedWebhookKey(webhookKeyResponse.data);
      } catch (error) {
        setGenerateWebhookKeyError(error instanceof Error ? error.message : 'Unable to generate webhook key.');
      } finally {
        setIsGeneratingWebhookKey(false);
      }
    };

    void generate();
  };

  const handleGenerateWebhookKeyConfirm = () => {
    if (generatedWebhookKey) {
      handleGenerateWebhookKeyResultClose();
      return;
    }

    handleCreateWebhookKey();
  };

  const handleCopyGeneratedWebhookKey = () => {
    if (!generatedWebhookKey?.token) {
      return;
    }

    if (!navigator.clipboard?.writeText) {
      setCopyWebhookKeyStatus('Clipboard API is not available.');
      return;
    }

    const copy = async () => {
      try {
        await navigator.clipboard.writeText(generatedWebhookKey.token);
        setCopyWebhookKeyStatus('Copied.');
      } catch {
        setCopyWebhookKeyStatus('Unable to copy the key.');
      }
    };

    void copy();
  };

  const handleCopyGeneratedTriggerToken = () => {
    if (!generatedTriggerToken?.token) {
      return;
    }

    if (!navigator.clipboard?.writeText) {
      setCopyTriggerTokenStatus('Clipboard API is not available.');
      return;
    }

    const copy = async () => {
      try {
        await navigator.clipboard.writeText(generatedTriggerToken.token);
        setCopyTriggerTokenStatus('Copied.');
      } catch {
        setCopyTriggerTokenStatus('Unable to copy the token.');
      }
    };

    void copy();
  };

  const handleRevokeWebhookKeyOpen = (webhookKey: JiraProject) => {
    setWebhookKeyToRevoke(webhookKey);
    setRevokeWebhookKeyError(undefined);
  };

  const handleRevokeWebhookKeyClose = () => {
    if (isRevokingWebhookKey) {
      return;
    }

    setWebhookKeyToRevoke(null);
    setRevokeWebhookKeyError(undefined);
  };

  const handleRevokeWebhookKey = () => {
    if (!selectedTeamId || !webhookKeyToRevoke) {
      return;
    }

    const teamId = selectedTeamId;
    const projectKey = webhookKeyToRevoke.projectKey;
    const requestId = latestTeamRequest.current;

    setIsRevokingWebhookKey(true);
    setRevokeWebhookKeyError(undefined);

    const revoke = async () => {
      try {
        await deleteWebhookKey(fesConfig, tokens, teamId, projectKey);
      } catch (error) {
        setRevokeWebhookKeyError(error instanceof Error ? error.message : 'Unable to revoke webhook key.');
        setIsRevokingWebhookKey(false);
        return;
      }

      setWebhookKeyToRevoke(null);
      try {
        await refreshWebhookKeys(teamId, requestId);
      } finally {
        setIsRevokingWebhookKey(false);
      }
    };

    void revoke();
  };

  const handleSave = () => {
    if (!selectedTeamId) {
      return;
    }

    if (!form.name.trim()) {
      setPanelError('Name is required.');
      return;
    }

    const currentInstallationId = typeof selectedTeam?.installationId === 'number' ? String(selectedTeam.installationId) : '';
    const nextInstallationId = form.installationId.trim();
    const installationIdChanged = nextInstallationId !== currentInstallationId;
    const parsedInstallationId = Number(nextInstallationId);
    const hasValidInstallationId =
      nextInstallationId.length > 0 &&
      /^\d+$/.test(nextInstallationId) &&
      Number.isSafeInteger(parsedInstallationId) &&
      parsedInstallationId > 0;

    if (installationIdChanged && !hasValidInstallationId) {
      setPanelError('Installation ID must be a positive integer.');
      return;
    }

    const jiraBaseUrl = form.jiraBaseUrl.trim().replace(/\/+$/, '');
    if (jiraBaseUrl && !/^https:\/\/[^\s]+$/i.test(jiraBaseUrl)) {
      setPanelError('Jira Base URL must be a valid HTTPS URL.');
      return;
    }
    if (form.jiraConnectionMode === 'dedicated' && !jiraBaseUrl) {
      setPanelError('A dedicated Jira connection requires a Jira Base URL.');
      return;
    }
    const originalJiraMode = effectiveJiraConnectionMode(selectedTeam?.jiraConnection);
    const originalJiraBaseUrl = (originalJiraMode === 'dedicated'
      ? selectedTeam?.jiraConnection?.baseUrl ?? ''
      : '')
      .trim()
      .replace(/\/+$/, '');
    const jiraConnectionChanged = form.jiraConnectionMode !== originalJiraMode
      || jiraBaseUrl !== originalJiraBaseUrl;
    const jiraConnection = selectedTeam?.jiraConnection || jiraConnectionChanged
      ? {
          mode: form.jiraConnectionMode,
          status: form.jiraConnectionMode === 'disabled' ? 'disabled' as const : 'active' as const,
          ...(form.jiraConnectionMode === 'dedicated' && jiraBaseUrl ? { baseUrl: jiraBaseUrl } : {}),
        }
      : undefined;
    setMode('saving');
    setPanelError(undefined);

    const save = async () => {
      try {
        const teamResponse = await updateTeam(fesConfig, tokens, selectedTeamId, {
          name: form.name.trim(),
          description: form.description,
          status: form.status,
          ...(jiraConnection ? { jiraConnection } : {}),
        });
        let savedTeam: Team = teamResponse.data || {
          ...(selectedTeam || { teamId: selectedTeamId, name: form.name.trim(), status: form.status }),
          name: form.name.trim(),
          description: form.description,
          status: form.status,
          ...(jiraConnection ? { jiraConnection } : {}),
        };

        if (installationIdChanged) {
          const installResponse = await installTeam(fesConfig, tokens, selectedTeamId, {
            installationId: parsedInstallationId,
          });
          savedTeam = installResponse.data || {
            ...savedTeam,
            installationId: parsedInstallationId,
          };
        }

        setSelectedTeam(savedTeam);
        setForm(teamToForm(savedTeam));
        setMode('read');
        onTeamSaved(savedTeam);
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : 'Unable to save team.');
        setMode('edit');
      }
    };

    void save();
  };

  const canGenerateWebhookKey =
    !generatedWebhookKey &&
    !areAgentsLoading &&
    !isGeneratingWebhookKey &&
    jiraProjectKeyPattern.test(createWebhookKeyForm.projectKey.trim().toUpperCase()) &&
    Boolean(createWebhookKeyForm.agentId);
  const canSaveCreateTeam =
    !isCreatingTeam &&
    Boolean(createForm.name.trim()) &&
    (!createForm.generateTriggerToken ||
      (!areAgentsLoading &&
        (createForm.triggerTokenChannel !== 'jira' ||
          !createForm.triggerTokenProjectKey.trim() ||
          jiraProjectKeyPattern.test(createForm.triggerTokenProjectKey.trim().toUpperCase())))) &&
    (!createForm.generateWebhookKey ||
      (!areAgentsLoading &&
        Boolean(createForm.installationId) &&
        jiraProjectKeyPattern.test(createForm.projectKey.trim().toUpperCase()) &&
        Boolean(createForm.agentId)));
  const hasGeneratedCreateCredential = !isCreatingTeam && Boolean(generatedWebhookKey || generatedTriggerToken);
  const createResultTitle =
    generatedWebhookKey && generatedTriggerToken
      ? 'Credentials generated'
      : generatedTriggerToken
        ? 'Trigger token generated'
        : 'Webhook key generated';
  const createResultCloseLabel =
    generatedWebhookKey && generatedTriggerToken
      ? 'Close generated credentials'
      : generatedTriggerToken
        ? 'Close generated trigger token'
        : 'Close generated webhook key';
  const normalizedListSearch = listSearch.trim().toLowerCase();
  const filteredTeams = normalizedListSearch
    ? teams.filter(team =>
        [team.name, team.description, team.teamId, team.status, team.installationId]
          .some(value => String(value ?? '').toLowerCase().includes(normalizedListSearch))
      )
    : teams;
  const routeTeamExists = Boolean(routeTeamId && teams.some(team => team.teamId === routeTeamId));

  return (
    <>
      {routeTeamId ? (
        <>
          <div className="app-detail-navigation">
            <Button icon="semantic-back" link onClick={() => navigate('/teams')} simple>
              Back to teams
            </Button>
          </div>
          {selectedTeam || isDetailLoading ? (
            <div ref={teamDetailRef} className="app-team-detail-tabs app-team-detail-tabs--standalone">
              <Tabs selected={activeTeamTab} onChangeSelected={setActiveTeamTab}>
                <Tabs.Header>
                  <Tabs.HeaderItem tabId="overview">Overview</Tabs.HeaderItem>
                  {session.isAdmin ? <Tabs.HeaderItem tabId="members">Members</Tabs.HeaderItem> : null}
                  <Tabs.HeaderItem tabId="github-triggers">GitHub triggers</Tabs.HeaderItem>
                  <Tabs.HeaderItem tabId="credentials">Credentials</Tabs.HeaderItem>
                </Tabs.Header>
                <Tabs.Content>
                  <Tabs.ContentItem tabId="overview">
                    <TeamDetailPanel
                      canEdit={canEdit}
                      error={panelError}
                      form={form}
                      isLoading={isDetailLoading}
                      mode={mode}
                      onCancel={handleCancel}
                      onEdit={handleEdit}
                      onFormChange={handleFormChange}
                      onSave={handleSave}
                      team={selectedTeam}
                    />
                  </Tabs.ContentItem>
                  {session.isAdmin && selectedTeamId ? (
                    <Tabs.ContentItem tabId="members">
                      <TeamMembersPanel fesConfig={fesConfig} tokens={tokens} teamId={selectedTeamId} />
                    </Tabs.ContentItem>
                  ) : null}
                  <Tabs.ContentItem tabId="github-triggers">
                    {selectedTeamId ? (
                      <GitHubPrTriggersPanel
                        key={selectedTeamId}
                        fesConfig={fesConfig}
                        teamId={selectedTeamId}
                        tokens={tokens}
                      />
                    ) : null}
                  </Tabs.ContentItem>
                  <Tabs.ContentItem tabId="credentials">
                    {selectedTeamId ? (
                      <TriggerTokensPanel key={selectedTeamId} fesConfig={fesConfig} teamId={selectedTeamId} tokens={tokens} />
                    ) : null}
                    {selectedTeam && hasActiveAzureDevOpsToken(selectedTeam) ? (
                      <AzureDevOpsPanel
                        key={selectedTeam.teamId}
                        fesConfig={fesConfig}
                        onSaved={setSelectedTeam}
                        team={selectedTeam}
                        tokens={tokens}
                      />
                    ) : null}
                    <div className="app-deprecated-credentials">
                      <p className="app-field-hint">Legacy webhook keys are deprecated. Use trigger tokens for new integrations.</p>
                      <WebhookKeysTable
                        error={webhookKeysError}
                        isLoading={areWebhookKeysLoading}
                        onGenerateWebhookKey={handleGenerateWebhookKeyOpen}
                        onRevokeWebhookKey={handleRevokeWebhookKeyOpen}
                        revokingProjectKey={isRevokingWebhookKey ? webhookKeyToRevoke?.projectKey : undefined}
                        webhookKeys={webhookKeys}
                      />
                    </div>
                  </Tabs.ContentItem>
                </Tabs.Content>
              </Tabs>
            </div>
          ) : routeTeamExists ? (
            <div className="app-loading" role="status">
              <Spinner center>Loading team...</Spinner>
            </div>
          ) : (
            <div className="app-empty-state">
              <strong>Team not found</strong>
              <p>The requested team is not available to your account.</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="app-table-toolbar">
            <Input
              aria-label="Search teams"
              className="app-table-search"
              onChange={event => setListSearch(event.target.value)}
              placeholder="Search teams"
              type="search"
              value={listSearch}
            />
            {canEdit ? <Button icon="semantic-plus" onClick={handleCreateOpen}>Create team</Button> : null}
          </div>
          <TeamsTable teams={filteredTeams} selectedTeamId={selectedTeamId} onSelectTeam={handleSelect} />
        </>
      )}
      {saveError ? (
        <Modal
          buttonCloseLabel="Close"
          buttonConfirmText="OK"
          onClose={handleSaveErrorClose}
          onConfirm={handleSaveErrorClose}
          shown
          status="error"
          title="Unable to save team"
        >
          {saveError}
        </Modal>
      ) : null}
      {isGenerateWebhookKeyModalOpen ? (
        <Modal
          buttonCancelLabel="Cancel generate webhook key"
          buttonCancelText={generatedWebhookKey ? undefined : 'Cancel'}
          buttonCloseLabel="Close"
          buttonConfirmLabel={generatedWebhookKey ? 'Close generated webhook key' : 'Generate webhook key'}
          buttonConfirmText={generatedWebhookKey ? 'OK' : isGeneratingWebhookKey ? 'Generating...' : 'Generate'}
          confirmationDisabled={generatedWebhookKey ? false : !canGenerateWebhookKey}
          onCancel={generatedWebhookKey ? undefined : handleGenerateWebhookKeyClose}
          onClose={generatedWebhookKey ? handleGenerateWebhookKeyResultClose : handleGenerateWebhookKeyClose}
          onConfirm={handleGenerateWebhookKeyConfirm}
          shown
          status={generatedWebhookKey ? 'warning' : undefined}
          title={generatedWebhookKey ? 'Webhook key generated' : 'Generate webhook key'}
        >
          {generatedWebhookKey ? (
            <div className="app-webhook-key-result">
              <div className="app-webhook-key-warning" role="alert">
                This key will only be displayed this time and can't be regenerated. Copy it before closing this dialog.
              </div>
              <FormField labelForId="generated-webhook-key-token" labelText="Generated key" type="textarea">
                <Textarea id="generated-webhook-key-token" value={generatedWebhookKey.token} readOnly rows={5} />
              </FormField>
              <div className="app-copy-row">
                <Button secondary onClick={handleCopyGeneratedWebhookKey}>
                  Copy generated key
                </Button>
                {copyWebhookKeyStatus ? (
                  <span className="app-copy-status" role="status">
                    {copyWebhookKeyStatus}
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {generateWebhookKeyError ? (
                <div className="app-result app-team-error" role="alert">
                  {generateWebhookKeyError}
                </div>
              ) : null}
              {agentsError ? (
                <div className="app-result app-team-error" role="alert">
                  {agentsError}
                </div>
              ) : null}
              {areAgentsLoading ? (
                <div className="app-detail-loading" role="status">
                  <Spinner center>Loading agents...</Spinner>
                </div>
              ) : null}
              <Form
                className="app-create-team-form"
                onSubmit={event => {
                  event.preventDefault();
                  handleCreateWebhookKey();
                }}
              >
                <FormField labelForId="create-webhook-key-project-key" labelText="Project Key" notion type="input">
                  <Input
                    id="create-webhook-key-project-key"
                    value={createWebhookKeyForm.projectKey}
                    onChange={event => handleCreateWebhookKeyFormChange('projectKey', event.target.value)}
                    readOnly={isGeneratingWebhookKey}
                  />
                </FormField>
                <FormField labelForId="create-webhook-key-agent" labelText="Agent" notion type="select">
                  <Select
                    id="create-webhook-key-agent"
                    value={createWebhookKeyForm.agentId}
                    onChange={event => handleCreateWebhookKeyFormChange('agentId', event.target.value)}
                    disabled={areAgentsLoading || isGeneratingWebhookKey}
                  >
                    <Select.Item value="" disabled>
                      Select an agent
                    </Select.Item>
                    {agents.map(agent => (
                      <Select.Item key={agent.agentId} value={agent.agentId}>
                        {agent.name ? `${agent.name} (${agent.agentId})` : agent.agentId}
                      </Select.Item>
                    ))}
                  </Select>
                </FormField>
                <FormField labelForId="create-webhook-key-description" labelText="Description" type="textarea">
                  <Textarea
                    id="create-webhook-key-description"
                    value={createWebhookKeyForm.description}
                    onChange={event => handleCreateWebhookKeyFormChange('description', event.target.value)}
                    readOnly={isGeneratingWebhookKey}
                    rows={4}
                  />
                </FormField>
              </Form>
            </>
          )}
        </Modal>
      ) : null}
      {webhookKeyToRevoke ? (
        <Modal
          buttonCancelLabel="Cancel revoke webhook key"
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={`Revoke webhook key ${webhookKeyToRevoke.projectKey}`}
          buttonConfirmText={isRevokingWebhookKey ? 'Revoking...' : 'Revoke'}
          confirmationDisabled={isRevokingWebhookKey}
          onCancel={handleRevokeWebhookKeyClose}
          onClose={handleRevokeWebhookKeyClose}
          onConfirm={handleRevokeWebhookKey}
          shown
          status="warning"
          title="Revoke webhook key"
        >
          <>
            {revokeWebhookKeyError ? (
              <div className="app-result app-team-error" role="alert">
                {revokeWebhookKeyError}
              </div>
            ) : null}
            <p className="app-modal-copy">
              Revoke webhook key <strong>{webhookKeyToRevoke.projectKey}</strong>? Jira requests using this key will no
              longer be accepted.
            </p>
          </>
        </Modal>
      ) : null}
      {isCreateModalOpen ? (
        <Modal
          buttonCancelLabel="Cancel create team"
          buttonCancelText={hasGeneratedCreateCredential ? undefined : 'Cancel'}
          buttonCloseLabel="Close"
          buttonConfirmLabel={hasGeneratedCreateCredential ? createResultCloseLabel : 'Save'}
          buttonConfirmText={hasGeneratedCreateCredential ? 'OK' : isCreatingTeam ? 'Saving...' : 'Save'}
          confirmationDisabled={hasGeneratedCreateCredential ? false : !canSaveCreateTeam}
          onCancel={hasGeneratedCreateCredential ? undefined : handleCreateClose}
          onClose={handleCreateClose}
          onConfirm={hasGeneratedCreateCredential ? handleCreateTeamResultClose : handleCreateTeam}
          shown
          status={hasGeneratedCreateCredential ? 'warning' : undefined}
          title={hasGeneratedCreateCredential ? createResultTitle : 'Create team'}
        >
          {hasGeneratedCreateCredential ? (
            <div className="app-webhook-key-result">
              <div className="app-webhook-key-warning" role="alert">
                These credentials will only be displayed this time and can't be retrieved later. Copy them before closing this dialog.
              </div>
              {generatedTriggerToken ? (
                <div className="app-generated-credential">
                  <FormField labelForId="create-team-generated-trigger-token" labelText="Generated trigger token" type="textarea">
                    <Textarea id="create-team-generated-trigger-token" value={generatedTriggerToken.token} readOnly rows={5} />
                  </FormField>
                  <div className="app-copy-row">
                    <Button secondary onClick={handleCopyGeneratedTriggerToken}>
                      Copy generated trigger token
                    </Button>
                    {copyTriggerTokenStatus ? (
                      <span className="app-copy-status" role="status">
                        {copyTriggerTokenStatus}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {generatedWebhookKey ? (
                <div className="app-generated-credential">
                  <FormField labelForId="create-team-generated-webhook-key-token" labelText="Generated key" type="textarea">
                    <Textarea id="create-team-generated-webhook-key-token" value={generatedWebhookKey.token} readOnly rows={5} />
                  </FormField>
                  <div className="app-copy-row">
                    <Button secondary onClick={handleCopyGeneratedWebhookKey}>
                      Copy generated key
                    </Button>
                    {copyWebhookKeyStatus ? (
                      <span className="app-copy-status" role="status">
                        {copyWebhookKeyStatus}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              {createError ? (
                <div className="app-result app-team-error" role="alert">
                  {createError}
                </div>
              ) : null}
              {installationsError ? (
                <div className="app-result app-team-error" role="alert">
                  {installationsError}
                </div>
              ) : null}
              {agentsError && (createForm.generateWebhookKey || createForm.generateTriggerToken) ? (
                <div className="app-result app-team-error" role="alert">
                  {agentsError}
                </div>
              ) : null}
              <Form
                className="app-create-team-form"
                onSubmit={event => {
                  event.preventDefault();
                  handleCreateTeam();
                }}
              >
                <FormField labelForId="create-team-name" labelText="Name" notion type="input">
                  <Input
                    id="create-team-name"
                    value={createForm.name}
                    onChange={event => handleCreateFormChange('name', event.target.value)}
                    readOnly={isCreatingTeam}
                  />
                </FormField>
                <FormField labelForId="create-team-description" labelText="Description" type="textarea">
                  <Textarea
                    id="create-team-description"
                    value={createForm.description}
                    onChange={event => handleCreateFormChange('description', event.target.value)}
                    readOnly={isCreatingTeam}
                    rows={3}
                  />
                </FormField>
                <FormField labelForId="create-team-installation-id" labelText="Installation ID" type="select">
                  <Select
                    id="create-team-installation-id"
                    value={createForm.installationId}
                    onChange={event => handleCreateFormChange('installationId', event.target.value)}
                    disabled={isCreatingTeam || areInstallationsLoading}
                  >
                    <Select.Item value="">No installation</Select.Item>
                    {unclaimedInstallations.map(installation => (
                      <Select.Item key={installation.installationId} value={String(installation.installationId)}>
                        {`${installation.installationId} - ${installation.accountLogin}`}
                      </Select.Item>
                    ))}
                  </Select>
                </FormField>
                {areInstallationsLoading ? (
                  <div className="app-detail-loading app-detail-loading--compact" role="status">
                    <Spinner center>Loading installations...</Spinner>
                  </div>
                ) : null}
                <div className="app-workflow-panel">
                  <Checkbox
                    id="create-team-generate-trigger-token"
                    checked={createForm.generateTriggerToken}
                    disabled={isCreatingTeam}
                    onChange={event => handleCreateGenerateTriggerTokenChange(event.target.checked)}
                  >
                    Generate trigger token
                  </Checkbox>
                  <p className="app-field-hint">Recommended for new Jira, Azure DevOps, and pipeline automations.</p>
                  {createForm.generateTriggerToken ? (
                    <div className="app-workflow-panel-body">
                      {areAgentsLoading ? (
                        <div className="app-detail-loading app-detail-loading--compact" role="status">
                          <Spinner center>Loading agents...</Spinner>
                        </div>
                      ) : null}
                      <FormField labelForId="create-team-trigger-channel" labelText="Trigger Token Channel" notion type="select">
                        <Select
                          id="create-team-trigger-channel"
                          value={createForm.triggerTokenChannel}
                          onChange={event => handleCreateTriggerTokenChannelChange(event.target.value as TriggerTokenChannel)}
                          disabled={isCreatingTeam}
                        >
                          <Select.Item value="jira">Jira</Select.Item>
                          <Select.Item value="azure-devops">Azure DevOps</Select.Item>
                          <Select.Item value="pipeline">Pipeline</Select.Item>
                        </Select>
                      </FormField>
                      {createForm.triggerTokenChannel === 'jira' ? (
                        <FormField labelForId="create-team-trigger-project-key" labelText="Trigger Token Project Key" type="input">
                          <Input
                            id="create-team-trigger-project-key"
                            value={createForm.triggerTokenProjectKey}
                            onChange={event => handleCreateFormChange('triggerTokenProjectKey', event.target.value)}
                            placeholder="Optional, e.g. PROJ"
                            readOnly={isCreatingTeam}
                          />
                        </FormField>
                      ) : null}
                      <FormField labelForId="create-team-trigger-default-agent" labelText="Default Agent" type="select">
                        <Select
                          id="create-team-trigger-default-agent"
                          value={createForm.triggerTokenDefaultAgentId}
                          onChange={event => handleCreateFormChange('triggerTokenDefaultAgentId', event.target.value)}
                          disabled={areAgentsLoading || isCreatingTeam}
                        >
                          <Select.Item value="">No default agent</Select.Item>
                          {agents.map(agent => (
                            <Select.Item key={agent.agentId} value={agent.agentId}>
                              {agent.name ? `${agent.name} (${agent.agentId})` : agent.agentId}
                            </Select.Item>
                          ))}
                        </Select>
                      </FormField>
                      <section className="app-trigger-token-agent-selector" aria-labelledby="create-team-trigger-agents-heading">
                        <div className="app-trigger-token-agent-selector-header">
                          <h3 id="create-team-trigger-agents-heading">Allowed Agents (optional)</h3>
                          <span>{createForm.triggerTokenAllowedAgentIds.length} selected</span>
                        </div>
                        <p className="app-field-hint">Leave empty to allow any team agent.</p>
                        <div className="app-trigger-token-agent-list" role="group" aria-labelledby="create-team-trigger-agents-heading">
                          {agents.map(agent => (
                            <Checkbox
                              key={agent.agentId}
                              id={`create-team-trigger-agent-${agent.agentId}`}
                              checked={createForm.triggerTokenAllowedAgentIds.includes(agent.agentId)}
                              disabled={areAgentsLoading || isCreatingTeam}
                              onChange={event => handleCreateTriggerTokenAgentToggle(agent.agentId, event.target.checked)}
                            >
                              <span className="app-agent-skill-option">
                                <span className="app-agent-skill-name">{agent.name || agent.agentId}</span>
                                <span className="app-agent-skill-id">{agent.agentId}</span>
                              </span>
                            </Checkbox>
                          ))}
                          {!areAgentsLoading && agents.length === 0 ? <p className="app-empty">No agents found.</p> : null}
                        </div>
                      </section>
                      <FormField labelForId="create-team-trigger-description" labelText="Trigger Token Description" type="textarea">
                        <Textarea
                          id="create-team-trigger-description"
                          value={createForm.triggerTokenDescription}
                          onChange={event => handleCreateFormChange('triggerTokenDescription', event.target.value)}
                          readOnly={isCreatingTeam}
                          rows={3}
                        />
                      </FormField>
                    </div>
                  ) : null}
                </div>
                <div className="app-workflow-panel">
                  <Checkbox
                    id="create-team-generate-webhook-key"
                    checked={createForm.generateWebhookKey}
                    disabled={isCreatingTeam}
                    onChange={event => handleCreateGenerateWebhookKeyChange(event.target.checked)}
                  >
                    Generate webhook key
                  </Checkbox>
                  <p className="app-deprecation-note">Deprecated: use a trigger token for new integrations. Generate a webhook key only for v1 users.</p>
                  {createForm.generateWebhookKey ? (
                    <div className="app-workflow-panel-body">
                      {areAgentsLoading ? (
                        <div className="app-detail-loading app-detail-loading--compact" role="status">
                          <Spinner center>Loading agents...</Spinner>
                        </div>
                      ) : null}
                      <FormField labelForId="create-team-webhook-project-key" labelText="Project Key" notion type="input">
                        <Input
                          id="create-team-webhook-project-key"
                          value={createForm.projectKey}
                          onChange={event => handleCreateFormChange('projectKey', event.target.value)}
                          readOnly={isCreatingTeam}
                        />
                      </FormField>
                      <FormField labelForId="create-team-webhook-agent" labelText="Agent" notion type="select">
                        <Select
                          id="create-team-webhook-agent"
                          value={createForm.agentId}
                          onChange={event => handleCreateFormChange('agentId', event.target.value)}
                          disabled={areAgentsLoading || isCreatingTeam}
                        >
                          <Select.Item value="" disabled>
                            Select an agent
                          </Select.Item>
                          {agents.map(agent => (
                            <Select.Item key={agent.agentId} value={agent.agentId}>
                              {agent.name ? `${agent.name} (${agent.agentId})` : agent.agentId}
                            </Select.Item>
                          ))}
                        </Select>
                      </FormField>
                      <FormField labelForId="create-team-webhook-description" labelText="Webhook Key Description" type="textarea">
                        <Textarea
                          id="create-team-webhook-description"
                          value={createForm.webhookKeyDescription}
                          onChange={event => handleCreateFormChange('webhookKeyDescription', event.target.value)}
                          readOnly={isCreatingTeam}
                          rows={3}
                        />
                      </FormField>
                    </div>
                  ) : null}
                </div>
              </Form>
            </>
          )}
        </Modal>
      ) : null}
    </>
  );
}
